// The way in to the texts: client and server import from shared/texts, never from a language file.
//
// German (de.ts) is the base language. A further language is one more file next to it (en.ts …)
// with the same keys and the same placeholders; language() checks that at build time — an unknown
// key or a different set of placeholders is a type error. Whatever a language leaves out falls
// back to German. There is only German today, and no switch.
import { de } from './de'

// The names inside {…} of a text, as a union: 'Rechnung {nr} an {name}' → 'nr' | 'name'. A brace
// that shows on screen stays a brace: '{{key}}' fills key and keeps the outer pair.
export type Placeholders<S> = S extends `${string}{${infer K}}${infer R}`
    ? (K extends `${string}{${infer I}` ? I : K) | Placeholders<R>
    : never

// Sets the values into a text. Every placeholder of the text must be given; a value goes in as
// String(value), exactly as a template literal would have put it.
export function fill<S extends string>(text: S, values: { [K in Placeholders<S>]: unknown }): string {
    const v = values as Record<string, unknown>
    return text.replace(/\{(\w+)\}/g, (whole, key: string) => (key in v ? String(v[key]) : whole))
}

// The text of a value from a list (status, category, module …): only the value is stored and
// compared, the text is looked up here. A value the list does not know shows as itself.
export function textOf(list: { readonly [value: string]: string }, value: string): string {
    return list[value] ?? value
}

// Singular and plural are two texts, { one, other }; this picks one.
export function plural<T extends { readonly one: string; readonly other: string }>(n: number, text: T): T['one'] | T['other'] {
    return n === 1 ? text.one : text.other
}

type SamePlaceholders<B extends string, L extends string> = [Placeholders<B>] extends [Placeholders<L>]
    ? [Placeholders<L>] extends [Placeholders<B>]
        ? L
        : never
    : never

// What a language file may contain: only keys German has, a text where German has a text, and
// per text the same placeholders.
export type CheckedLanguage<B, L> = B extends string
    ? L extends string
        ? SamePlaceholders<B, L>
        : never
    : { [K in keyof L]: K extends keyof B ? CheckedLanguage<B[K], L[K]> : never }

// A further language is written as `export const en = language({ … })`.
export function language<const L extends CheckedLanguage<typeof de, L>>(file: L): L {
    return file
}

// German beneath, the language on top.
function withFallback<T>(base: T, over: unknown): T {
    if (over === undefined || over === null) return base
    if (typeof base !== 'object' || base === null || Array.isArray(base)) return over as T
    const out: Record<string, unknown> = { ...(base as Record<string, unknown>) }
    for (const [k, v] of Object.entries(over as Record<string, unknown>)) {
        out[k] = withFallback((base as Record<string, unknown>)[k], v)
    }
    return out as T
}

const LANGUAGES: Record<string, unknown> = { de }
export const LANGUAGE = 'de'

export const texts: typeof de = withFallback(de, LANGUAGES[LANGUAGE])

export const {
    SALE_STATE_LABELS, SALE_KIND_LABELS, INVOICE_STATUS_LABELS, INVOICE_TAB_NAME,
    SUBSCRIPTION_STATUS_LABELS, SUBSCRIPTION_STATUS_SINCE, SUBSCRIPTION_STALE, CUSTOMER_STALE,
    INVOICE_DRAFT_STALE, STALE_RELOAD, SUBSCRIPTION_REACTIVATE_HINT, FIELD_ERROR_TEXTS,
    CURRENCY_SETTING_TEXTS, MODULE_LABELS, CAP_LABELS, CAP_SHORT, DOC_CATEGORY_LABELS,
    TAX_DECISION_REASONS, CHECK_MARKS, CHECK_MARK_DETAIL, BRAND_NAMES, CLIENT_BRAND_NAMES,
    SALUTATION_LABELS, TEMPLATE_BRAND_LABELS, LABEL_PRESET_NAMES, LABEL_PRESET_OPTION, ENVELOPE_NAMES, ENVELOPE_OPTION, APP_TEXTS, ROUTES_ANALYTICS_TEXTS, ROUTES_AUTH_TEXTS,
    ROUTES_AUTHOR_PORTAL_API_TEXTS, ROUTES_AUTHOR_REGISTRY_API_TEXTS, ROUTES_AUTHORS_TEXTS,
    ROUTES_BRAND_CONDITIONS_TEXTS, ROUTES_CATALOG_TEXTS, ROUTES_CERTIFICATES_TEXTS,
    ROUTES_CERTIFICATES_API_TEXTS, ROUTES_CHECKLIST_TEXTS, ROUTES_CHECKOUT_TEXTS,
    ROUTES_CUSTOMERS_TEXTS, ROUTES_DOCUMENTS_TEXTS, ROUTES_EXPENSE_CONFIG_TEXTS, ROUTES_EXPORTS_TEXTS,
    ROUTES_HEALTH_TEXTS, ROUTES_INGEST_TEXTS, ROUTES_INGEST_ACTIONS_TEXTS,
    ROUTES_INVOICE_PDF_API_TEXTS, ROUTES_PUBLIC_DOCUMENTS_TEXTS, ROUTES_INVOICES_TEXTS, ROUTES_INVOICE_TEMPLATES_TEXTS,
    ROUTES_MAIL_FRAMES_TEXTS, ROUTES_MOLLIE_TEXTS, ROUTES_PAYPAL_TEXTS, ROUTES_REPORTS_TEXTS,
    ROUTES_ROLES_TEXTS, ROUTES_SETTINGS_TEXTS, ROUTES_STRIPE_TEXTS, ROUTES_SUBSCRIPTIONS_TEXTS,
    ROUTES_TODOS_TEXTS, ROUTES_TRANSACTIONS_TEXTS, ROUTES_USERS_TEXTS, ROUTES_VAT_TEXTS,
    ROUTES_WEBHOOK_TEXTS, ROUTES_WEBHOOKS_TEXTS, WEBHOOKS_EVENTS_TEXTS,
    SERVICES_PAYMENTS_MOLLIE_STATEMENT_TEXTS, SERVICES_PAYMENTS_PSP_STATEMENT_PDF_TEXTS,
    SERVICES_PAYMENTS_STRIPE_STATEMENT_TEXTS, SERVICES_PAYMENTS_MOLLIE_PROVIDER_TEXTS, SERVICES_PAYMENTS_UPGRADE_PRORATION_TEXTS,
    SERVICES_PAYMENTS_STRIPE_PROVIDER_TEXTS, SERVICES_PAYMENTS_REFUND_INVOICE_TEXTS,
    SERVICES_PAYMENTS_PROCESS_PAYMENT_EVENT_TEXTS, SERVICES_AUTHORS_STATEMENT_PDF_TEXTS,
    SERVICES_AUTHORS_STATEMENTS_TEXTS, SERVICES_AUTHORS_SEPA_TEXTS, SERVICES_AUTHORS_VIES_TEXTS,
    SERVICES_AUTHORS_REGISTRY_TEXTS, SERVICES_AUTHORS_SALES_TEXTS, SERVICES_AUTHORS_CATALOG_TEXTS,
    SERVICES_LLM_EXTRACT_TEXTS, SERVICES_LEGACY_IMPORT_V1_TEXTS, SERVICES_LEGACY_IMPORT_RUN_TEXTS,
    SERVICES_PRINT_ONLINEBRIEF24_TEXTS, SERVICES_PRINT_DISPATCH_TEXTS,
    SERVICES_PRINT_MOCK_PROVIDER_TEXTS, SERVICES_CERTIFICATES_PDF_TEXTS,
    SERVICES_CERTIFICATES_LAYOUT_TEXTS, SERVICES_CERTIFICATES_DISPATCH_TEXTS,
    SERVICES_CERTIFICATES_ADDRESS_PDF_TEXTS, SERVICES_REPORT_TEXTS, SERVICES_EXPORT_TEXTS,
    SERVICES_CHECKLIST_TEXTS, SERVICES_PAYPAL_TEXTS, SERVICES_PDF_TEXTS, SERVICES_STRIPE_REPORTS_TEXTS,
    SERVICES_MAILER_TEXTS, SERVICES_MAIL_TEXTS_TEXTS, SERVICES_DISPATCH_TEXTS, SERVICES_DOCUMENTS_TEXTS,
    SERVICES_ABO_BILLING_TEXTS, SERVICES_AUTH_TEXTS, SERVICES_BRAND_CONFIG_TEXTS, SHARED_COUNTRIES_TEXTS,
    SERVICES_BULK_TRASH_TEXTS, SERVICES_INGEST_TEXTS, SERVICES_SETTINGS_TEXTS, SERVICES_STRIPE_TEXTS,
    SERVICES_STRIPE_WEBHOOK_TEXTS, SERVICES_SUBSCRIPTIONS_TEXTS, SERVICES_VITALPILZE_WEBHOOK_TEXTS,
    SERVICES_VP_DELIVERY_QUEUE_TEXTS, SERVICES_VP_TRANSPORT_TEXTS, SERVICES_WEBHOOKS_TEXTS,
    SERVICES_GO_LIVE_CUT_TEXTS, SERVICES_NINOX_ABO_IMPORT_TEXTS, SERVICES_INVOICES_TEXTS, SERVICES_ZUGFERD_GENERATE_TEXTS,
    SERVICES_PDF_TEMPLATE_INTERPRETER_TEXTS, SERVICES_PDF_TEMPLATE_XML_TEXTS,
    SERVICES_PDF_TEMPLATE_FROM_INVOICE_TEXTS, SERVICES_PDF_TEMPLATE_RENDER_TEXTS, EMAIL_FRAMES_TEXTS,
    UTILS_IBAN_TEXTS, UTILS_ENV_PAIRING_TEXTS, CORE_SEED_TEXTS, CORE_STALE_TEXTS,
    MODELS_BRAND_CONFIG_TEXTS, SETTINGS_TEXT_DEFAULTS, LAYOUT_LAYOUT_MANAGER_TEXTS,
    HOOKS_USE_SERVER_LIST_TEXTS, DASHBOARD_WIDGETS_TEXTS, PAGES_DASHBOARD_TEXTS, CLIENT_APP_TEXTS,
    PAGES_LOGIN_TEXTS, PAGES_PROFILE_TEXTS, PAGES_USER_FORM_TEXTS, PAGES_USERS_LIST_TEXTS,
    PAGES_ROLE_EDITOR_TEXTS, PAGES_ROLES_LIST_TEXTS, PAGES_LOGS_VIEWER_TEXTS, PAGES_TRASH_VIEW_TEXTS,
    COMPONENTS_CONFIRM_DIALOG_TEXTS, COMPONENTS_PAGINATION_TEXTS, COMPONENTS_LIST_PANEL_TEXTS,
    COMPONENTS_PASSWORD_STRENGTH_METER_TEXTS, COMPONENTS_DRAWER_TEXTS, COMPONENTS_PASSWORD_INPUT_TEXTS,
    COMPONENTS_SECRET_INPUT_TEXTS, COMPONENTS_BULK_TRASH_TEXTS, COMPONENTS_RICH_TEXT_EDITOR_TEXTS,
    COMPONENTS_SEARCH_SELECT_TEXTS, COMPONENTS_COLUMN_MANAGER_TEXTS, PAGES_INVOICE_FORM_TEXTS,
    PAGES_INVOICES_LIST_TEXTS, PAGES_SUBSCRIPTIONS_LIST_TEXTS, PAGES_SUBSCRIPTION_FORM_TEXTS,
    PAGES_TRANSACTIONS_LIST_TEXTS, PAGES_CUSTOMER_FORM_TEXTS, PAGES_CUSTOMERS_LIST_TEXTS,
    COMPONENTS_LINE_ITEMS_EDITOR_TEXTS, COMPONENTS_CUSTOMER_PICKER_TEXTS,
    COMPONENTS_COUNTRY_PICKER_TEXTS, COMPONENTS_SEND_MAIL_DRAWER_TEXTS, CLIENT_VAT_MARK_TEXTS,
    CLIENT_API_TEXTS, PAGES_FILE_VIEWER_TEXTS, PAGES_AUTHOR_CATALOG_TEXTS, PAGES_AUTHORS_LIST_TEXTS,
    PAGES_CATALOG_ITEM_FORM_TEXTS, COMPONENTS_BRAND_MAIL_TEXTS_TEXTS, PAGES_CERTIFICATE_DISPATCH_LIST_TEXTS, PAGES_AUTHOR_FORM_TEXTS,
    PAGES_MONTHLY_EXPORT_TEXTS, PAGES_AUTHOR_STATEMENTS_TEXTS, PAGES_SETTINGS_TEXTS,
    PAGES_VAT_SETTINGS_TEXTS, PAGES_MAIL_TEMPLATES_TEXTS, PAGES_TEMPLATES_TEXTS,
    PAGES_INVOICE_TEMPLATE_EDITOR_TEXTS, PAGES_INVOICE_TEMPLATES_LIST_TEXTS,
    PAGES_WEBHOOKS_MANAGER_TEXTS, PAGES_EXPENSE_MAPPING_ADMIN_TEXTS, COMPONENTS_MAIL_FRAME_EDITOR_TEXTS,
} = texts
